/* global globalThis */
import axios from "axios";

export const baseURL = "https://hubspotfrontdoor-api.marcusevans.com"; 


const isBrowser = () => typeof globalThis !== "undefined" && !!globalThis?.localStorage;

const getStorageItem = (key) => {
  if (!isBrowser()) return null;
  return globalThis.localStorage.getItem(key);
};

const setStorageItem = (key, value) => {
  if (!isBrowser()) return;
  globalThis.localStorage.setItem(key, value);
};

const clearStorage = () => {
  if (!isBrowser()) return;
  globalThis.localStorage.clear();
};

const redirectToLogin = () => {
  if (!isBrowser()) return;
  globalThis.location.href = "/";
};

class Network {
  constructor(baseURLValue) {
    this.client = axios.create({
      baseURL: baseURLValue,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // ✅ Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = getStorageItem("access_token");
        if (token) {
          config.headers = {
            ...(config.headers),
            Authorization: `Bearer ${token}`,
          };
        }
        return config;
      },
      (error) => {
        // ✅ Sonar S7746: prefer throw
        throw error instanceof Error ? error : new Error(String(error));
      }
    );

    // ✅ Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config;

        if (error?.response) {
          // ✅ 401 Unauthorized → refresh
          if (
            error.response.status === 401 &&
            originalRequest &&
            !originalRequest._retry
          ) {
            console.error("Unauthorized, refreshing token...");
            originalRequest._retry = true;

            try {
              const newTokens = await this.refreshToken();

              const { access_token, refresh_token } = newTokens.ResponseData;
              setStorageItem("access_token", access_token);
              setStorageItem("refresh_token", refresh_token);

              return this.client(originalRequest);
            } catch (refreshError) {
              console.error("Error refreshing token:", refreshError);
              clearStorage();
              redirectToLogin();
            }
          }

          // ✅ 403 Forbidden
          if (error.response.status === 403) {
            console.error("Forbidden, clearing local storage...");
            clearStorage();
            redirectToLogin();
          }
        }

        // ✅ Sonar S7746: prefer throw
        throw error instanceof Error ? error : new Error(String(error));
      }
    );
  }

  // ✅ Refresh token call
  async refreshToken() {
    const refreshToken = getStorageItem("refresh_token");

    if (!refreshToken) {
      throw new Error("No refresh token found");
    }

    try {
      const response = await axios.post(`${baseURL}/auth/refresh`, null, {
        params: { refresh_token: refreshToken },
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data?.IsSuccess) {
        return response.data;
      }

      throw new Error("Token refresh failed");
    } catch (error) {
      console.error("Error refreshing access token:", error);
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  // ✅ Methods
  get(url, params, config = {}) {
    return this.client.get(url, { params, ...config });
  }

  post(url, data) {
    return this.client.post(url, data);
  }

  put(url, data) {
    return this.client.put(url, data);
  }

  delete(url) {
    return this.client.delete(url);
  }
}

// ✅ Export instance
const api = new Network(baseURL);
export default api;
